import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Container,
  Row,
  Col,
  Badge,
} from "reactstrap"
import { FaLinkedin, FaInstagram, FaFacebook, FaCheckCircle, FaPen } from "react-icons/fa"
import { MdWork, MdErrorOutline, MdChevronLeft, MdChevronRight } from "react-icons/md"
import NaukariIcon from "./../../assets/images/Naukari_Icon.jpg"
import SunEditor from "suneditor-react"
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"
import axios from "axios"
import Swal from "sweetalert2"

const PLATFORM_META = {
  linkedin: { label: "LinkedIn", icon: <FaLinkedin />, color: "#0A66C2" },
  instagram: { label: "Instagram", icon: <FaInstagram />, color: "#E1306C" },
  facebook: { label: "Facebook", icon: <FaFacebook />, color: "#1877F2" },
  naukri: { label: "Naukri", icon: <MdWork />, color: "#2E3A59" },
}

const ACCENT = "#0A66C2"

const cardStyle = {
  border: "1px solid #e6e8ec",
  borderRadius: "14px",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  background: "#fff",
}

const panelHeaderStyle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "#101828",
  marginBottom: "0.9rem",
  letterSpacing: "-0.01em",
}

const mutedStyle = { color: "#667085", fontSize: "0.85rem" }

// Converts "• "-separated plain text/html into a real bulleted list so it
// renders as distinct points instead of one dense run-on paragraph.
const formatPreviewHtml = html => {
  if (!html) return html
  const parts = html.split("•")
  if (parts.length <= 1) return html // no bullets found, leave as-is

  const [intro, ...items] = parts
  const listItems = items
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => `<li style="margin-bottom: 6px;">${item}</li>`)
    .join("")

  return `
    <div style="margin-bottom: 0.5rem;">${intro}</div>
    <ul style="padding-left: 1.25rem; margin: 0; line-height: 1.6;">${listItems}</ul>
  `
}

const SocialMediaPost2 = ({
  jobData,
  postToLinkedIn,
  setPostToLinkedIn,
  setJobData,
  selectedLinkedInOrg,
  setSelectedLinkedInOrg,
  setSelectedTemplateContent,
  getsingleJobOrderid,
  setSelectedPreviewId,
  selectedPreviewId,
  disabledPreview,
}) => {
  const navigate = useNavigate()

  const [platforms, setPlatforms] = useState({
    linkedin: jobData?.post_to_linkedin ? "yes" : "no",
    instagram: null,
    facebook: null,
    naukri: null,
  })

  const userInfos = JSON.parse(sessionStorage.getItem("user-info"))
  const intialcompanyId = userInfos.user_details.companies_data[0].company_id

  const [linkedinPreview, setLinkedinPreview] = useState({})
  const [loadingPreview, setLoadingPreview] = useState(false)
  const templateKeys = Object.keys(linkedinPreview || {})
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editedText, setEditedText] = useState("")
  const [isRemoving, setIsRemoving] = useState(false)
  const [companyId, setCompanyId] = useState(intialcompanyId)
  const [successModal, setSuccessModal] = useState(false)
  const [showLinkedInModal, setShowLinkedInModal] = useState(false)
  const [linkedInOrgs, setLinkedInOrgs] = useState([])

  const isPosted = Boolean(jobData?.post_to_linkedin)
  const isThisTemplatePosted =
    isPosted && Number(jobData?.linkedin_template) === Number(selectedPreviewId)

  const templateEntries = Object.entries(linkedinPreview || {})
  const totalTemplates = templateEntries.length

  const handleSelectPreview = (key, previewObj) => {
    setSelectedTemplate(key)
    setSelectedTemplateContent(previewObj.html)
    setSelectedPreviewId(previewObj.id)
  }

  const handleEditOpen = () => {
    const key = templateKeys[activeIndex]
    setEditedText(linkedinPreview[key]?.html || "")
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setIsEditing(false)
    setEditedText("")
  }

  const saveContentPreview = async content => {
    try {
      const response = await axios.post("/api/v1/content-preview/", {
        content,
        preview_id: selectedPreviewId,
      })
      return response.data
    } catch (error) {
      console.error("Error saving content preview", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save content preview",
      })
      throw error
    }
  }

  const handleEditSave = async () => {
    try {
      const key = templateKeys[activeIndex]
      const data = await saveContentPreview(editedText)
      const savedHtml = data?.html_preview
      if (!savedHtml) throw new Error("No html_preview returned from API")

      setLinkedinPreview(prev => ({
        ...prev,
        [key]: { ...prev[key], html: savedHtml },
      }))
      setSelectedTemplateContent(savedHtml)
      setEditedText(savedHtml)
      setIsEditing(false)

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Content saved successfully",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error(error)
    }
  }

  const fetchLinkedInJobText = async () => {
    try {
      setLoadingPreview(true)
      const res = await axios.get(`/api/v1/job-texts/?job_id=${jobData?.job_id}`)
      const previews = res?.data?.previews || {}
      const htmlPreviews = Object.fromEntries(
        Object.entries(previews).map(([key, value]) => [
          key,
          { id: value.id, html: value.html },
        ])
      )
      setLinkedinPreview(htmlPreviews)
    } catch (error) {
      console.error("Failed to fetch LinkedIn preview", error)
      setLinkedinPreview({})
    } finally {
      setLoadingPreview(false)
    }
  }

  const fetchLinkedInOrgs = async () => {
    try {
      const response = await axios.get(`/api/v1/linkedin-details/?company_id=${companyId}`)
      setLinkedInOrgs(response.data.results || [])
    } catch (error) {
      console.error("Error fetching LinkedIn organizations:", error)
    }
  }

  const handleRadioChange = async (platform, value) => {
    setPlatforms(prev => ({ ...prev, [platform]: value }))
    if (platform === "linkedin") {
      if (value === "yes") {
        setPostToLinkedIn(true)
        await fetchLinkedInJobText()
      } else {
        setPostToLinkedIn(false)
        setLinkedinPreview({})
      }
    }
  }

  const handleRemoveLinkedInPost = async () => {
    try {
      setIsRemoving(true)
      const jobId = jobData.job_id
      if (!jobId) {
        console.error("No job ID found.")
        return
      }
      const response = await axios.delete(`/api/v1/delete-linkedin-post/?job_id=${jobId}`)
      if (response.status === 200) {
        setJobData(prev => ({ ...prev, post_to_linkedin: false, linkedin_post_id: null }))
        await getsingleJobOrderid(jobId)
        setPostToLinkedIn(false)
        Swal.fire({
          title: "Success!",
          text: "The LinkedIn post has been removed successfully.",
          icon: "success",
          confirmButtonText: "OK",
        })
      } else {
        console.error("Failed to remove LinkedIn post.")
        Swal.fire({
          title: "Error!",
          text: "Failed to remove the LinkedIn post.",
          icon: "error",
          confirmButtonText: "OK",
        })
      }
    } catch (error) {
      console.error("Error removing LinkedIn post:", error)
      const errorMessage = error?.response?.data?.message || "Failed to delete LinkedIn post"
      Swal.fire({ title: "Error!", text: errorMessage, icon: "error", confirmButtonText: "OK" })
    } finally {
      setIsRemoving(false)
    }
  }

  const handleLinkedInOrgSelect = org => {
    setSelectedLinkedInOrg(org)
    setShowLinkedInModal(false)
  }

  const handlePrev = () => {
    setActiveIndex(prev => (prev === 0 ? totalTemplates - 1 : prev - 1))
    setIsEditing(false)
  }

  const handleNext = () => {
    setActiveIndex(prev => (prev === totalTemplates - 1 ? 0 : prev + 1))
    setIsEditing(false)
  }

  useEffect(() => {
    if (jobData?.post_to_linkedin !== undefined) {
      setPostToLinkedIn(!!jobData.post_to_linkedin)
    }
  }, [jobData?.post_to_linkedin])

  useEffect(() => {
    if (jobData?.job_id && platforms.linkedin === "yes") {
      fetchLinkedInJobText()
    }
  }, [jobData?.job_id, platforms.linkedin])

  useEffect(() => {
    const keys = Object.keys(linkedinPreview || {})
    if (keys.length > 0 && selectedTemplate === null) {
      setActiveIndex(0)
      setSelectedTemplate(keys[0])
    }
  }, [linkedinPreview, selectedTemplate])

  useEffect(() => {
    if (templateEntries.length > 0) {
      const [key, previewObj] = templateEntries[activeIndex]
      handleSelectPreview(key, previewObj)
    }
  }, [activeIndex, linkedinPreview])

  const PlatformToggle = ({ platformKey, disabled }) => {
    const meta = PLATFORM_META[platformKey]
    const value = platforms[platformKey]
    const isYes = value === "yes"
    return (
      <div
        style={{
          ...cardStyle,
          borderColor: isYes ? meta.color : "#e6e8ec",
          padding: "0.9rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: "0.6rem",
          columnGap: "0.75rem",
          transition: "border-color 0.15s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <span
            style={{
              color: meta.color,
              fontSize: "1.15rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2.1rem",
              height: "2.1rem",
              borderRadius: "10px",
              background: `${meta.color}14`,
              flexShrink: 0,
            }}
          >
            {meta.icon}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{meta.label}</div>
            <div style={{ ...mutedStyle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Post this job to {meta.label}
            </div>
          </div>
        </div>
        <div className="d-flex gap-3 flex-shrink-0">
          <div className="form-check form-check-inline m-0">
            <Input
              type="radio"
              name={platformKey}
              checked={isYes}
              disabled={disabled}
              onChange={() => handleRadioChange(platformKey, "yes")}
            />
            <label className="form-check-label ms-1">Yes</label>
          </div>
          <div className="form-check form-check-inline m-0">
            <Input
              type="radio"
              name={platformKey}
              checked={value === "no"}
              disabled={disabled}
              onChange={() => handleRadioChange(platformKey, "no")}
            />
            <label className="form-check-label ms-1">No</label>
          </div>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content1 allact-tabs1" style={{ background: "#f7f8fa" }}>
        <Container fluid>
          {/* Header */}
          <div className="mb-3 pt-2">
            <h4 style={{ fontWeight: 700, marginBottom: "0.2rem", letterSpacing: "-0.01em" }}>
              Social Media Job Posting
            </h4>
            <div style={mutedStyle}>
              Review how this job will appear before publishing it to your social channels.
            </div>
          </div>

          <Row className="g-3">
            {/* LEFT: Channels */}
            <Col xs={12} lg={4}>
              <div style={{ ...cardStyle, padding: "1.25rem", height: "100%" }}>
                <div style={panelHeaderStyle}>Channels</div>
                <div className="d-flex flex-column gap-2">
                  <PlatformToggle platformKey="linkedin" disabled={disabledPreview} />
                  <PlatformToggle
                    platformKey="instagram"
                    disabled={jobData?.post_to_linkedin || disabledPreview}
                  />
                  <PlatformToggle
                    platformKey="facebook"
                    disabled={jobData?.post_to_linkedin || disabledPreview}
                  />
                  <PlatformToggle
                    platformKey="naukri"
                    disabled={jobData?.post_to_linkedin || disabledPreview}
                  />
                </div>
              </div>
            </Col>

            {/* RIGHT: Preview */}
            <Col xs={12} lg={8}>
              <div style={{ ...cardStyle, padding: "1.25rem" }}>
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <FaLinkedin color={ACCENT} size={18} />
                  <span style={{ ...panelHeaderStyle, marginBottom: 0 }}>LinkedIn Job Preview</span>
                  {isPosted && (
                    <Badge style={{ background: "#12B76A", fontWeight: 500 }}>Posted</Badge>
                  )}
                </div>
                <div style={{ ...mutedStyle, marginBottom: "0.9rem" }}>
                  Preview only — final appearance may vary slightly on LinkedIn.
                </div>

                {platforms.linkedin !== "yes" ? (
                  <div
                    style={{
                      ...cardStyle,
                      border: "1px dashed #d0d5dd",
                      boxShadow: "none",
                      padding: "2.5rem 1rem",
                      textAlign: "center",
                      color: "#98a2b3",
                    }}
                  >
                    LinkedIn posting is not enabled for this job order.
                  </div>
                ) : loadingPreview ? (
                  <OrgTableShimmer columns={4} rows={6} titleWidth="220px" />
                ) : totalTemplates === 0 ? (
                  <div
                    style={{
                      ...cardStyle,
                      border: "1px dashed #d0d5dd",
                      boxShadow: "none",
                      padding: "2.5rem 1rem",
                      textAlign: "center",
                      color: "#98a2b3",
                    }}
                  >
                    No generated content available yet.
                  </div>
                ) : (
                  <div
                    style={{
                      ...cardStyle,
                      padding: "1.25rem",
                      background: isThisTemplatePosted ? "#f0f9f4" : "#fff",
                      borderColor: isThisTemplatePosted ? "#a6e9c4" : "#e6e8ec",
                    }}
                  >
                    {/* Toolbar: prev / counter / next+edit — all in normal flow, nothing overlaps */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={handlePrev}
                        className="d-flex align-items-center gap-1"
                      >
                        <MdChevronLeft size={16} /> Previous
                      </Button>

                      <span style={{ fontWeight: 600, ...mutedStyle }}>
                        Template {activeIndex + 1} of {totalTemplates}
                      </span>

                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          outline
                          color="secondary"
                          onClick={handleNext}
                          className="d-flex align-items-center gap-1"
                        >
                          Next <MdChevronRight size={16} />
                        </Button>
                        {!isEditing && (
                          <Button
                            size="sm"
                            color="primary"
                            outline
                            onClick={handleEditOpen}
                            className="d-flex align-items-center gap-1"
                            style={{ borderRadius: "8px" }}
                          >
                            <FaPen size={11} /> Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-start gap-3"
                      style={{
                        maxHeight: "50vh",
                        overflow: "auto",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                        padding: "1rem",
                        background: "#fbfbfc",
                      }}
                    >
                      <Input
                        type="radio"
                        name="linkedinTemplate"
                        style={{ marginTop: "4px" }}
                        checked={selectedTemplate === templateEntries[activeIndex]?.[0]}
                        onChange={() => {
                          const [key, previewObj] = templateEntries[activeIndex]
                          handleSelectPreview(key, previewObj)
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {!isEditing ? (
                          <div
                            style={{ lineHeight: 1.6 }}
                            dangerouslySetInnerHTML={{
                              __html: formatPreviewHtml(templateEntries[activeIndex]?.[1]?.html),
                            }}
                          />
                        ) : (
                          <>
                            <SunEditor
                              setContents={editedText}
                              onChange={setEditedText}
                              height="250px"
                              setOptions={{
                                buttonList: [
                                  ["undo", "redo"],
                                  ["bold", "underline", "italic"],
                                  ["fontSize", "formatBlock"],
                                  ["list", "align"],
                                  ["link"],
                                  ["removeFormat"],
                                ],
                              }}
                            />
                            <div className="d-flex justify-content-end gap-2 mt-2">
                              <Button size="sm" outline onClick={handleEditCancel}>
                                Cancel
                              </Button>
                              <Button size="sm" color="primary" onClick={handleEditSave}>
                                Save
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {isThisTemplatePosted && (
                      <div className="d-flex justify-content-center gap-2 mt-3">
                        <Button size="sm" color="success" disabled>
                          Posted
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          outline
                          onClick={handleRemoveLinkedInPost}
                          disabled={isRemoving}
                        >
                          {isRemoving ? "Removing..." : "Remove Post"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Success modal (kept for compatibility with external triggers) */}
      <Modal isOpen={successModal} toggle={() => setSuccessModal(false)} centered>
        <ModalHeader toggle={() => setSuccessModal(false)}>Success!</ModalHeader>
        <ModalBody>Your job has been posted successfully!</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setSuccessModal(false)}>
            OK
          </Button>
        </ModalFooter>
      </Modal>

      {/* LinkedIn organization selection modal */}
      <Modal isOpen={showLinkedInModal} toggle={() => setShowLinkedInModal(false)} centered size="md">
        <ModalHeader toggle={() => setShowLinkedInModal(false)}>
          Select LinkedIn Organization
        </ModalHeader>
        <ModalBody>
          {linkedInOrgs.length > 0 ? (
            linkedInOrgs.map(org => (
              <div key={org.id} className="form-check mb-2">
                <input
                  type="radio"
                  id={`linkedinOrg_${org.id}`}
                  name="linkedinOrg"
                  checked={selectedLinkedInOrg?.id === org.id}
                  onChange={() => handleLinkedInOrgSelect(org)}
                  className="form-check-input"
                />
                <label className="form-check-label" htmlFor={`linkedinOrg_${org.id}`}>
                  {org.organisation_name}
                </label>
              </div>
            ))
          ) : (
            <p>Loading LinkedIn organizations...</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowLinkedInModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => setShowLinkedInModal(false)}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default SocialMediaPost2